import { scalingPx } from 'util/css';
import { pxToEm } from 'util/pxToEm';
import {
  Badge,
  Box,
  BoxProps,
  Breadcrumbs,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import React, { ReactElement, ReactNode } from 'react';
import { useIsFullWidth, useIsLoggedIn } from 'hooks';

import { useRouter } from 'next/router';
import clsx from 'clsx';

import { Header } from 'components/app';

import { Routes } from 'services/routes';
import { Image, Link } from 'components/lib';

import { ButtonLink, EventFeedbackDrawer, NotificationDrawer } from 'components/ui';
import { useNotificationsApi } from 'services/api/hooks/useNotificationsApi.hook';
import useSWR from 'swr';
import { MainLayoutSidebarProps, Sidebar } from './MainLayout.Sidebar';
import { Breadcrumb } from './MainLayout.Breadcrumb';
import { MainLayoutTopProps, Top } from './MainLayout.Top';

const MENU_ICONS = {
  home: '/icons/menu/home-nav-active.svg',
  homeOutline: '/icons/menu/home-nav.svg',
  explore: '/icons/menu/explore-nav-active.svg',
  exploreOutline: '/icons/menu/explore-nav.svg',
  events: '/icons/menu/checkin-nav-active.svg',
  eventsOutline: '/icons/menu/checkin-nav.svg',
  notifications: '/icons/menu/notifications-nav-active.svg',
  notificationsOutline: '/icons/menu/notifications-nav.svg',
  menu: '/icons/menu/menu-nav.svg',
};

export interface MainLayoutProps extends BoxProps {
  children?: ReactNode;
  hideSideMenu?: boolean;
  hideMobileBottomNav?: boolean;
  backgroundColor?: string;
  disableSidebarShadow?: boolean;
  contentChildrenStyles?: SxProps;
}

interface MainLayout {
  (props: MainLayoutProps): ReactElement;
  Sidebar: typeof Sidebar;
  Breadcrumb: typeof Breadcrumb;
}

// eslint-disable-next-line complexity
export const MainLayout = ({
  children,
  contentChildrenStyles = {},
  hideSideMenu,
  hideMobileBottomNav,
  backgroundColor,
  disableSidebarShadow,
  ...boxProps
}: MainLayoutProps): ReactElement => {
  const isFullWidth = useIsFullWidth();
  const isLoggedIn = useIsLoggedIn();

  const childrenArr = React.Children.toArray(children);

  const theme = useTheme();

  // Notification drawer state for mobile
  const [isNotificationDrawerOpen, setNotificationDrawerOpen] = React.useState(false);

  // Fetch unread notification count for mobile badge
  const notificationsApi = useNotificationsApi();
  const { data: unreadCountData } = useSWR(
    isLoggedIn ? 'notifications-unread-count' : null,
    async () => {
      const result = await notificationsApi.getUnreadCount();
      if (!result.success) return { count: 0 };
      return result.value;
    },
    {
      revalidateOnFocus: true,
      refreshInterval: 30000, // Refresh every 30 seconds
    },
  );
  const unreadCount = unreadCountData?.count || 0;

  let active: 'home' | 'explore' | 'events' | 'profile' | 'notifications' | undefined;

  const router = useRouter();
  const path = router?.asPath;
  if (path === '/') active = 'home';
  else if (/^\/explore\/?/.test(path) || path.startsWith('/organizations'))
    active = 'explore';
  else if (/^\/events\/?/.test(path)) active = 'events';
  else if (/^\/(profile|settings)\/?/.test(path)) active = 'profile';

  const isHome = active === 'home';
  const isExplore = active === 'explore';
  const isNotifications = isNotificationDrawerOpen;
  const isEvents = active === 'events';
  const isProfile = active === 'profile';
  const showAbsoluteSideMenu =
    ['/'].includes((path || '').split('?')[0]) && !hideSideMenu;

  // Get feedback event ID from query params
  const feedbackEventId = router.query.feedback as string | undefined;

  const sidebars = childrenArr.filter((child: ReactNode) => {
    return React.isValidElement(child) && child.type === MainLayout.Sidebar;
  }) as ReactElement<MainLayoutSidebarProps>[];

  const leftSidebars = sidebars.filter(
    (sidebar: ReactElement<MainLayoutSidebarProps>) => {
      return React.isValidElement(sidebar) && sidebar.props.side === 'left';
    },
  );

  const rightSidebars = sidebars.filter(
    (sidebar: ReactElement<MainLayoutSidebarProps>) => {
      return React.isValidElement(sidebar) && sidebar.props.side === 'right';
    },
  );

  const breadcrumbs = childrenArr.filter((child: ReactNode) => {
    return React.isValidElement(child) && child.type === MainLayout.Breadcrumb;
  }) as ReactElement<MainLayoutSidebarProps>[];

  const hasBreadcrumbs = breadcrumbs.length > 0;

  const topSections = childrenArr.filter((child: ReactNode) => {
    return React.isValidElement(child) && child.type === MainLayout.Top;
  }) as ReactElement<MainLayoutTopProps>[];

  const contentChildren = childrenArr.filter((child: ReactNode) => {
    return (
      !React.isValidElement(child) ||
      !([
        MainLayout.Sidebar,
        MainLayout.Breadcrumb,
        MainLayout.Top,
      ] as unknown[]).includes(child.type)
    );
  });

  const breadcrumbPadding = scalingPx(20, 55.5);
  const breadcrumbContainerHeight = 40;

  return (
    <Box
      display="flex"
      minHeight="100%"
      flexDirection="column"
      style={{ background: theme.palette.background.default }}
      {...boxProps}
    >
      <Header />
      {topSections}
      <Box display="flex">
        {leftSidebars.map((sidebar, i) => (
          <Box
            key={i}
            sx={
              showAbsoluteSideMenu
                ? {
                    flex: '0 0 auto',
                    zIndex: 100,
                    position: 'fixed',
                    overflowY: 'auto',
                    height: 'calc(100% - 60px)',
                    top: '60px',
                    boxShadow: disableSidebarShadow
                      ? 'none'
                      : 'rgba(0, 0, 0, 0.16) 0px 3px 26px',
                  }
                : {
                    flex: '0 0 auto',
                    zIndex: 100,
                  }
            }
          >
            {sidebar}
          </Box>
        ))}

        <Box flex="1 1" minWidth={0} style={{ backgroundColor: backgroundColor }}>
          {hasBreadcrumbs && isFullWidth && (
            <Box
              height={breadcrumbContainerHeight}
              display="flex"
              alignItems="center"
              borderBottom="1px solid #dedede"
              px={breadcrumbPadding}
              fontSize={13}
            >
              <Breadcrumbs
                aria-label="breadcrumb"
                sx={{
                  fontSize: '1em',
                  color: theme => theme.palette.neutral.darkest,
                  height: 'inherit',
                  lineHeight: 0,
                  '& ol': {
                    flexWrap: 'nowrap',
                    height: 'inherit',
                    '& li': {
                      lineHeight: '19px',
                      maxHeight: 38,
                      minWidth: 26,
                      textOverflow: 'clip',
                      outline: 'none',
                      '& a': {
                        color: 'inherit',
                        outlineOffset: 6,
                        '&:focus-visible': {
                          outline: theme =>
                            `2px auto ${theme.palette.primary.main}`,
                        },
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      },
                    },
                  },
                }}
                separator={
                  <Box
                    border="solid #dedede"
                    width={breadcrumbContainerHeight / Math.SQRT2}
                    height={breadcrumbContainerHeight / Math.SQRT2}
                    style={{
                      borderWidth: '0 1px 1px 0',
                      transformOrigin: 'center',
                      transform: `rotate(-45deg) translateX(-${
                        breadcrumbContainerHeight / 8
                      }px) translateY(-${breadcrumbContainerHeight / 8}px)`,
                    }}
                  />
                }
              >
                {breadcrumbs}
              </Breadcrumbs>
            </Box>
          )}

          <Box
            ml={{ xs: 0, md: showAbsoluteSideMenu ? 360 : 0 }}
            sx={{
              pt: { xs: 0, md: hasBreadcrumbs ? 30 : 60 },
              pb: 90,
              px: { xs: 20, md: 60, lg: 60 },
              ...contentChildrenStyles,
            }}
          >
            {contentChildren}
          </Box>
        </Box>
        {rightSidebars.map((sidebar, i) => (
          <Box key={i} flex="0 1" zIndex={100}>
            {sidebar}
          </Box>
        ))}
      </Box>
      {!hideMobileBottomNav && (
        <Box
          display={{ xs: 'flex', md: 'none' }}
          position="fixed"
          left={0}
          right={0}
          bottom={0}
          justifyContent="space-evenly"
          style={{ background: '#fff' }}
          pt={14}
          pb={11}
          boxShadow="0 -1px 6px rgba(0,0,0,0.16)"
          zIndex={200}
          sx={{
            '& .MainLayout-mobileNavItem': {
              color: theme => theme.palette.primary.main,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
              width: 86,
              textDecoration: 'none',
            },
            '& .MainLayout-mobileNavText': {
              fontSize: 12,
              color: '#A7A7A7',
            },
            '& .MainLayout-mobileNavText.active': {
              color: '#383838',
            },
          }}
        >
          {isLoggedIn && (
            <>
              <Link href={Routes.home()} className="MainLayout-mobileNavItem">
                <Box lineHeight={'22px'}>
                  <Image
                    alt={'Home'}
                    src={isHome ? MENU_ICONS.home : MENU_ICONS.homeOutline}
                  />
                </Box>
                <div
                  className={clsx('MainLayout-mobileNavText', {
                    active: isHome,
                  })}
                >
                  <Typography fontSize={10} fontWeight={500} color={'#383838'}>
                    Home
                  </Typography>
                </div>
              </Link>
              <Link
                href={Routes.eventTickets()}
                className="MainLayout-mobileNavItem"
              >
                <Box lineHeight={'22px'}>
                  <Image
                    alt={'Check In'}
                    src={
                      isEvents ? MENU_ICONS.events : MENU_ICONS.eventsOutline
                    }
                  />
                </Box>
                <div
                  className={clsx('MainLayout-mobileNavText', {
                    active: isEvents,
                  })}
                >
                  <Typography fontSize={10} fontWeight={500} color={'#383838'}>
                    Check In
                  </Typography>
                </div>
              </Link>
              <Link
                href={Routes.explore()}
                className="MainLayout-mobileNavItem"
              >
                <Box lineHeight={'22px'}>
                  <Image
                    alt={'Explore'}
                    src={
                      isExplore ? MENU_ICONS.explore : MENU_ICONS.exploreOutline
                    }
                  />
                </Box>
                <div
                  className={clsx('MainLayout-mobileNavText', {
                    active: isExplore,
                  })}
                >
                  <Typography fontSize={10} fontWeight={500} color={'#383838'}>
                    Explore
                  </Typography>
                </div>
              </Link>
              <Box
                className="MainLayout-mobileNavItem"
                onClick={() => setNotificationDrawerOpen(true)}
                sx={{ position: 'relative' }}
              >
                <Badge
                  badgeContent={unreadCount > 0 ? unreadCount : undefined}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      right: -2,
                      top: 1,
                      minWidth: 20,
                      height: 20,
                      padding: '0 4px',
                      border: '1px solid #fff',
                      borderRadius: '50%',
                      background: '#FF385C',
                      color: '#fff',
                      fontSize: pxToEm(12),
                      fontWeight: 600,
                    },
                  }}
                >
                  <Box lineHeight={'22px'}>
                    <Image
                      alt={'Notifications'}
                      style={{ width: 20 }}
                      src={
                        isNotifications
                          ? MENU_ICONS.notifications
                          : MENU_ICONS.notificationsOutline
                      }
                    />
                  </Box>
                </Badge>
                <div
                  className={clsx('MainLayout-mobileNavText', {
                    active: isNotifications,
                  })}
                >
                  <Typography fontSize={10} fontWeight={500} color={'#383838'}>
                    Notifications
                  </Typography>
                </div>
              </Box>
              <Link
                href={Routes.settings()}
                className="MainLayout-mobileNavItem"
              >
                <Box lineHeight={'22px'}>
                  <Image alt={'Menu'} src={MENU_ICONS.menu} />
                </Box>
                <div
                  className={clsx('MainLayout-mobileNavText', {
                    active: isProfile,
                  })}
                >
                  <Typography fontSize={10} fontWeight={500} color={'#383838'}>
                    More
                  </Typography>
                </div>
              </Link>
            </>
          )}
          {!isLoggedIn && (
            <>
              <Box mr={30}>
                <ButtonLink
                  href={Routes.signUp()}
                  hoverEffect="none"
                  minHeight={37}
                >
                  Create Account
                </ButtonLink>
              </Box>
              <ButtonLink
                href={Routes.logIn()}
                variant="vibrant"
                hoverEffect="none"
                minHeight={37}
              >
                Log In
              </ButtonLink>
            </>
          )}
        </Box>
      )}
      {feedbackEventId && (
        <EventFeedbackDrawer
          eventId={feedbackEventId}
          open={!!feedbackEventId}
          onOpen={() => {
            // Ensure query param is set
            if (!router.query.feedback) {
              void router.replace(
                {
                  pathname: router.pathname,
                  query: { ...router.query, feedback: feedbackEventId },
                },
                undefined,
                { shallow: true },
              );
            }
          }}
          onClose={() => {
            // Remove query params when closing
            const { feedback, fromNotification: _, ...restQuery } = router.query;
            void router.replace(
              {
                pathname: router.pathname,
                query: restQuery,
              },
              undefined,
              { shallow: true },
            );
          }}
        />
      )}
      {isLoggedIn && (
        <NotificationDrawer
          open={isNotificationDrawerOpen}
          onOpen={() => setNotificationDrawerOpen(true)}
          onClose={() => setNotificationDrawerOpen(false)}
        />
      )}
    </Box>
  );
};

MainLayout.Sidebar = Sidebar;
MainLayout.Breadcrumb = Breadcrumb;
MainLayout.Top = Top;
